(ns frontend.view.settings2.security.mcp-admin.view
  "MCP admin view — tenant admin can see all MCP connections and revoke them."
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [frontend.actions.tenant :as tenant-actions]
   [frontend.db :as app-db]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.view.modals.confirmation.view :as confirmation-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.settings2.security.mcp-admin.events :as events]
   [frontend.view.settings2.security.mcp-admin.sub :as sub]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.grid.view :as grid]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.components.select.select :as select]
   [ui.dom-globals :as dg]
   [ui.state.session.sub :as session-sub]
   [ui.util :as ui-util]))

(defn- account-display-name
  [{:keys [account/first-name account/last-name account/email]}]
  (let [full-name (string/trim (str first-name " " last-name))]
    (if (< 1 (count full-name))
      full-name
      (or email (loc/tr :settings/unknown-connection)))))

(defn- open-revoke-confirmation!
  [connection-id connection-name account-name]
  (modals/open-modal!
   confirmation-modal/modal-key
   {:title          (loc/tr :settings/mcp-admin-revoke-title)
    :body           [:div
                     [:span (loc/tr :settings/mcp-admin-revoke-confirmation)]
                     [:br]
                     [:b (or connection-name (loc/tr :settings/unknown-connection))]
                     (when account-name
                       [:span (str " (" account-name ")")])]
    :confirm-text   (loc/tr :revoke)
    :skip-checkbox? true
    :on-confirm     #(rf/dispatch [::events/revoke-connection connection-id])}))

(defn- open-terminate-session-confirmation!
  [session-id account-name]
  (modals/open-modal!
   confirmation-modal/modal-key
   {:title          (loc/tr :settings/mcp-terminate-session-title)
    :body           [:div
                     [:span (loc/tr :settings/mcp-terminate-session-confirmation)]
                     (when account-name
                       [:span (str " (" account-name ")")])]
    :confirm-text   (loc/tr :settings/mcp-terminate)
    :skip-checkbox? true
    :on-confirm     #(rf/dispatch [::events/terminate-session session-id])}))

(defn- open-undo-confirmation!
  [tx-id undo-count]
  (modals/open-modal!
   confirmation-modal/modal-key
   {:title          (loc/tr :settings/mcp-undo-title)
    :body           [:div
                     [:span (loc/tr :settings/mcp-undo-description)]
                     [:br]
                     [:b (loc/tr :settings/mcp-undo-confirm [undo-count])]
                     [:span (str " — tx " tx-id)]]
    :confirm-text   (loc/tr :settings/mcp-undo-confirm [undo-count])
    :skip-checkbox? true
    :on-confirm     #(rf/dispatch [::events/execute-undo tx-id])}))

(defn- sessions-table
  [sessions]
  (let [tenant               @(rf/subscribe [::session-sub/tenant])
        format-ts            #(ui-util/display-date-timestamp tenant %)
        terminating-id       @(rf/subscribe [::sub/terminating-session-id])]
    [:div
     {:class "border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
     [:table
      {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
      [:thead
       [:tr
        {:class "*:px-3 *:py-3 *:text-left *:text-sm *:font-semibold *:text-base-light-text"}
        [:th (loc/tr :settings/mcp-sessions-col-user)]
        [:th (loc/tr :settings/mcp-sessions-col-started)]
        [:th (loc/tr :settings/mcp-sessions-col-last-active)]
        [:th ""]]]
      [:tbody
       {:class "divide-y divide-solid divide-base-light-border-paler"}
       (for [session sessions
             :let [{:keys [session-id created-at last-seen-at]} session
                   acct-name (account-display-name session)
                   busy?     (= terminating-id session-id)]]
         ^{:key session-id}
         [:tr
          {:class "*:px-3 *:py-3 *:text-sm *:text-base-light-text *:align-middle"}
          [:td acct-name]
          [:td (if created-at (format-ts created-at) "—")]
          [:td (if last-seen-at (format-ts last-seen-at) "—")]
          [:td
           {:class "text-right"}
           [button/sm-danger-solid
            {:on-click #(open-terminate-session-confirmation! session-id acct-name)
             :disabled? busy?}
            (if busy?
              [:i.fal.fa-spinner.fa-spin]
              (loc/tr :settings/mcp-terminate))]]])]]]))

(defn- open-revoke-all-confirmation!
  []
  (modals/open-modal!
   confirmation-modal/modal-key
   {:title          (loc/tr :settings/revoke-all-mcp-connections-title)
    :body           [:div [:span (loc/tr :settings/revoke-all-mcp-connections-confirmation)]]
    :confirm-text   (loc/tr :settings/revoke-all)
    :skip-checkbox? true
    :on-confirm     #(rf/dispatch [::events/revoke-all-connections])}))

(defn- write-access-toggle
  "Toggle that lets the TA allow or deny write access for all MCP connections."
  [_tenant]
  (let [saving? (r/atom false)
        error   (r/atom nil)]
    (fn [tenant]
      (let [enabled? (boolean (:tenant/mcp-allow-write? tenant))
            tenant-id (:db/id tenant)]
        [:div
         {:class "flex flex-col gap-2 p-3 border border-solid border-base-light-border-paler rounded"}
         [:div
          {:class "flex items-center justify-between"}
          [:div
           {:class "flex flex-col gap-0.5"}
           [:span {:class "font-semibold text-sm"} (loc/tr :settings/mcp-allow-write-label)]
           [:span {:class "text-xs text-base-light-text-paler"} (loc/tr :settings/mcp-allow-write-description)]]
          [:div
           {:class "flex items-center gap-2"}
           (when @saving? [:i.fal.fa-spinner.fa-spin.text-sm.text-base-light-text-paler])
           [tooltip/view
            {}
            [:span
             {:class    (str "relative inline-flex h-5 w-9 items-center rounded-full transition-colors "
                             "focus:outline-none cursor-pointer border-0 "
                             (if enabled? "bg-primary-regular" "bg-base-light-border-paler"))
              :role     "switch"
              :tabIndex 0
              :aria-checked enabled?
              :on-click (fn []
                          (when-not @saving?
                            (reset! saving? true)
                            (reset! error nil)
                            (swap! app-db/tenant assoc :tenant/mcp-allow-write? (not enabled?))
                            (tenant-actions/*->update!
                             tenant-id
                             :tenant/mcp-allow-write?
                             (not enabled?)
                             (fn [_]
                               (reset! saving? false)
                               (rf/dispatch [::events/load-tool-permissions {:quiet? true}]))
                             (fn [err]
                               (swap! app-db/tenant assoc :tenant/mcp-allow-write? enabled?)
                               (reset! saving? false)
                               (reset! error (or (:message err) "Failed to save"))
                               (logging/error "Failed to update mcp-allow-write?" err)))))}
             [:span
              {:class (str "inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform "
                           (if enabled? "translate-x-4" "translate-x-0.5"))}]]
            [:span {:class "text-sm"}
             (if enabled?
               (loc/tr :settings/mcp-allow-write-enabled-tooltip)
               (loc/tr :settings/mcp-allow-write-disabled-tooltip))]]]]
         (when @error
           [:div {:class "text-danger-regular text-xs"} @error])]))))

(defn- required-tool?
  [{:keys [required?]}]
  (true? required?))

(defn- required-tool-pill
  []
  [:span
   {:class "inline-flex items-center rounded border border-solid border-primary-light bg-primary-pale px-2 py-1 text-xs font-semibold text-primary-regular"
    :title "Required for MCP sessions and cannot be blocked"}
   "Required"])

(defn- access-label
  [access]
  (case access
    nil ""
    :mcp.tool.source/module-read "MCP module missing"
    :mcp.tool.source/module-write "MCP write module missing"
    :mcp.tool.source/tenant-write-disabled "Tenant write disabled"
    :mcp.tool.source/token-read-only "Token read-only"
    :mcp.tool.source/team-override "Team override"
    :mcp.tool.source/tool-default "Tool default"
    (name access)))

(defn- effective-tool-kind
  [{:keys [tool-kind read-only? destructive?]}]
  (or tool-kind
      (cond
        read-only? :mcp.tool-kind/read
        destructive? :mcp.tool-kind/potentially-destructive
        :else :mcp.tool-kind/write)))

(defn- tool-default-allowed?
  [{:keys [default-allowed?] :as tool}]
  (if (boolean? default-allowed?)
    default-allowed?
    (not (contains? #{:mcp.tool-kind/potentially-destructive
                      :mcp.tool-kind/always-destructive}
                    (effective-tool-kind tool)))))

(defn- tool-kind-label
  [tool]
  (case (effective-tool-kind tool)
    :mcp.tool-kind/read "Read"
    :mcp.tool-kind/write "Write"
    :mcp.tool-kind/potentially-destructive "Potentially destructive"
    :mcp.tool-kind/always-destructive "Always destructive"
    "Write"))

(defn- write-tool?
  [tool]
  (not= :mcp.tool-kind/read (effective-tool-kind tool)))

(defn- effective-team-access-allowed?
  [permission tool]
  (case permission
    :mcp.tool/allow true
    :mcp.tool/block false
    (tool-default-allowed? tool)))

(defn- permission-for-effective-access
  [allowed? tool]
  (if (= allowed? (tool-default-allowed? tool))
    :mcp.tool/default
    (if allowed?
      :mcp.tool/allow
      :mcp.tool/block)))

(defn- write-disabled-reason
  [tool {:keys [team mcp-write-allowed?]}]
  (when (write-tool? tool)
    (cond
      (not (:mcp-write-enabled? team))
      :mcp.team/read-only

      (not mcp-write-allowed?)
      :mcp.tenant/write-disabled)))

(defn- write-disabled-title
  [reason]
  (case reason
    :mcp.team/read-only
    (loc/tr :settings/mcp-team-read-only-tool-tooltip)

    :mcp.tenant/write-disabled
    (loc/tr :settings/mcp-allow-write-disabled-tooltip)

    nil))

(defn- selected-team
  [data selected-team-id]
  (or (->> (:teams data)
           (filter (fn [team]
                     (= (:id team) selected-team-id)))
           first)
      (first (:teams data))))

(defn- selected-user
  [data selected-user-id]
  (or (->> (:users data)
           (filter (fn [user]
                     (= (:id user) selected-user-id)))
           first)
      (first (:users data))))

(defn- team-options
  [teams]
  (mapv (fn [team]
          {:value (:id team)
           :label (:name team)})
        teams))

(defn- empty-permissions-state
  [{:keys [title body action-label on-action]}]
  [:div
   {:class "p-6 border border-solid border-base-light-border-paler rounded text-center text-base-light-text-paler text-sm"}
   [:div {:class "font-semibold text-base-light-text mb-1"} title]
   [:div body]
   (when action-label
     [:div {:class "mt-3"}
      [button/sm-secondary-hollow
       {:on-click on-action}
       action-label]])])

(defn- tool-row-data
  [tools {:keys [team mcp-write-allowed?]}]
  (let [permissions (:tool-permissions team)]
    (mapv (fn [{:keys [name] :as tool}]
            (let [permission (get permissions name :mcp.tool/default)
                  write-disabled-reason (write-disabled-reason
                                         tool
                                         {:team               team
                                          :mcp-write-allowed? mcp-write-allowed?})
                  write-disabled? (boolean write-disabled-reason)]
              (assoc tool
                     :info (or (:info tool) (:description tool) name)
                     :permission permission
                     :team-access-allowed? (if write-disabled?
                                             false
                                             (effective-team-access-allowed?
                                              permission
                                              tool))
                     :write-disabled? write-disabled?
                     :write-disabled-reason write-disabled-reason
                     :required? (required-tool? tool)
                     :type (tool-kind-label tool))))
          tools)))

(defn- permission-cell-renderer
  [{:keys [team-id saving?]} params]
  (let [data (js->clj (.-data params) :keywordize-keys true)
        tool-name (:name data)
        allowed? (:team-access-allowed? data)
        write-disabled? (:write-disabled? data)
        write-disabled-reason (:write-disabled-reason data)
        next-permission (permission-for-effective-access
                         (not allowed?)
                         data)]
    (r/as-element
     (if (:required? data)
       [required-tool-pill]
       [:button
        {:type      "button"
         :class     (str "inline-flex items-center gap-2 border-0 bg-transparent p-0 text-sm "
                         (if write-disabled?
                           "text-base-light-text-paler"
                           "text-base-light-text"))
         :disabled  (boolean (or saving? write-disabled?))
         :title     (cond
                      write-disabled?
                      (write-disabled-title write-disabled-reason)

                      allowed?
                      "Allowed for this team"

                      :else
                      "Disallowed for this team")
         :on-click  (fn [_]
                      (when-not write-disabled?
                        (rf/dispatch [::events/set-team-tool-permission
                                      team-id
                                      tool-name
                                      next-permission])))
         :aria-pressed allowed?}
        [:span
         {:class (str "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors "
                      (cond
                        saving? "cursor-wait opacity-60 "
                        write-disabled? "cursor-not-allowed opacity-60 "
                        :else "cursor-pointer ")
                      (if allowed? "bg-primary-regular" "bg-base-light-border-paler"))}
         [:span
          {:class (str "inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform "
                       (if allowed? "translate-x-4" "translate-x-0.5"))}]]
        [:span {:class "whitespace-nowrap"}
         (if allowed? "Allowed" "Disallowed")]]))))

(defn- info-cell-renderer
  [params]
  (let [info (or (.-value params) "")]
    (r/as-element
     [:span
      {:class "block truncate text-sm"
       :title info}
      info])))

(defn- team-permission-columns
  [{:keys [team-id saving?]}]
  [{:field :name
    :headerName "Tool"
    :flex 1
    :minWidth 240
    :sortable true
    :filter true}
   {:field :info
    :headerName "Info"
    :flex 2
    :minWidth 280
    :sortable true
    :filter true
    :cellRenderer info-cell-renderer}
   {:field :type
    :headerName "Type"
    :width 140
    :sortable true
    :filter true}
   {:field :team-access-allowed?
    :headerName "Team access"
    :width 170
    :cellRenderer (partial permission-cell-renderer
                           {:team-id team-id
                            :saving? saving?})}])

(defn- users-row-data
  [users]
  (mapv (fn [user]
          (assoc user
                 :team-names (if (seq (:teams user))
                               (->> (:teams user)
                                    (map :name)
                                    (string/join ", "))
                               "No team")))
        users))

(defn- users-columns
  []
  [{:field :display-name
    :headerName "User"
    :flex 1
    :minWidth 180
    :sortable true
    :filter true}
   {:field :email
    :headerName "Email"
    :flex 1
    :minWidth 220
    :sortable true
    :filter true}
   {:field :team-names
    :headerName "Teams"
    :flex 1
    :minWidth 240
    :sortable true
    :filter true}
   {:field :allowed-tools-count
    :headerName "Allowed"
    :width 120
    :sortable true}
   {:field :blocked-tools-count
    :headerName "Blocked"
    :width 120
    :sortable true}])

(defn- user-tool-row-data
  [user]
  (mapv (fn [tool]
          (assoc tool
                 :info (or (:info tool) (:description tool) (:name tool))
                 :type (tool-kind-label tool)
                 :effective-access (if (:allowed? tool) "Allowed" "Blocked")
                 :source-label (access-label (:source tool))))
        (:tools user)))

(defn- user-tool-columns
  []
  [{:field :name
    :headerName "Tool"
    :flex 1
    :minWidth 240
    :sortable true
    :filter true}
   {:field :info
    :headerName "Info"
    :flex 2
    :minWidth 280
    :sortable true
    :filter true
    :cellRenderer info-cell-renderer}
   {:field :type
    :headerName "Type"
    :width 140
    :sortable true
    :filter true}
   {:field :effective-access
    :headerName "Access"
    :width 140
    :sortable true
    :filter true}
   {:field :source-label
    :headerName "Source"
    :width 180
    :sortable true
    :filter true}])

(defn- permissions-tabs
  [active-tab]
  [:div {:class "flex gap-2"}
   [button/sm-secondary-hollow
    {:on-click (fn [_]
                 (rf/dispatch [::events/set-permissions-tab :teams]))
     :disabled? (= active-tab :teams)}
    "Teams"]
   [button/sm-secondary-hollow
    {:on-click (fn [_]
                 (rf/dispatch [::events/set-permissions-tab :users]))
     :disabled? (= active-tab :users)}
    "Users"]])

(defn- team-permissions-view
  [data {:keys [selected-team-id saving? mcp-write-allowed?]}]
  (let [team (selected-team data selected-team-id)]
    [:div {:class "flex flex-col gap-3"}
     (cond
       (empty? (:teams data))
       [empty-permissions-state
        {:title        "No tenant teams found"
         :body         "Create or enable tenant teams before configuring MCP tool permissions."
         :action-label "Open Teams"
         :on-action    (fn [_]
                         (routing/set-view! [:account-settings :tab-key "teams"]))}]

       (empty? (:tools data))
       [empty-permissions-state
        {:title "No MCP tools found"
         :body  "MCP tool permissions will appear here after the server returns registered MCP tools."}]

       :else
       [:<>
        [:div {:class "flex flex-col gap-1 max-w-sm"}
         [:label {:class "text-xs font-medium text-base-light-text-paler"} "Tenant team"]
         [select/view
          {:options (team-options (:teams data))
           :value   (:id team)
           :on-change (fn [team-id]
                        (rf/dispatch [::events/select-team team-id]))}]]
        (when team
          [:div {:class "text-sm text-base-light-text-paler"}
           "Editing MCP permissions for "
           [:span {:class "font-semibold text-base-light-text"} (:name team)]])
        [:div {:class "min-h-[320px]"}
         [grid/ag-grid-enterprise
          {:theme                grid/theme-quartz
           :rowData              (tool-row-data
                                  (:tools data)
                                  {:team               team
                                   :mcp-write-allowed? mcp-write-allowed?})
           :columnDefs           (team-permission-columns
                                  {:team-id (:id team)
                                   :saving? saving?})
           :defaultColDef        {:sortable true
                                  :filter true
                                  :resizable true
                                  :menuTabs ["generalMenuTab"]}
           :domLayout            :autoHeight}]]])]))

(defn- user-permissions-view
  [data selected-user-id]
  (let [user (selected-user data selected-user-id)]
    [:div {:class "flex flex-col gap-3"}
     [:div {:class "min-h-[220px]"}
      [grid/ag-grid-enterprise
       {:theme              grid/theme-quartz
        :rowData            (users-row-data (:users data))
        :columnDefs         (users-columns)
        :defaultColDef      {:sortable true
                             :filter true
                             :resizable true
                             :menuTabs ["generalMenuTab"]}
        :row-selection      {:mode "singleRow"}
        :on-selection-changed (fn [event]
                                (when-let [selected (first (js->clj (dg/ag-grid-selected-rows event)
                                                                    :keywordize-keys true))]
                                  (rf/dispatch [::events/select-user (:id selected)])))
        :domLayout          :autoHeight}]]
     (when user
       [:div {:class "flex flex-col gap-2"}
        [:div {:class "font-semibold text-base"} (:display-name user)]
        (when (empty? (:teams user))
          [:div
           {:class "flex gap-2 rounded border border-solid border-warning-light-border bg-warning-light p-3 text-sm text-warning-light-text"}
           [:i.fal.fa-exclamation-triangle.mt-0.5]
           [:div {:class "font-semibold"} "No team membership"]])
        [:div {:class "min-h-[320px]"}
         ^{:key (str "mcp-user-tools-" (:id user))}
         [grid/ag-grid-enterprise
          {:theme                grid/theme-quartz
           :rowData              (user-tool-row-data user)
           :columnDefs           (user-tool-columns)
           :defaultColDef        {:sortable true
                                  :filter true
                                  :resizable true
                                  :menuTabs ["generalMenuTab"]}
           :domLayout            :autoHeight}]]])]))

(defn- tool-permissions-section
  []
  (let [loading? @(rf/subscribe [::sub/permissions-loading?])
        saving? @(rf/subscribe [::sub/permissions-saving?])
        error @(rf/subscribe [::sub/permissions-error])
        data @(rf/subscribe [::sub/permissions-data])
        active-tab @(rf/subscribe [::sub/permissions-tab])
        selected-team-id @(rf/subscribe [::sub/selected-team-id])
        selected-user-id @(rf/subscribe [::sub/selected-user-id])
        tenant @(rf/subscribe [::session-sub/tenant])
        mcp-write-allowed? (boolean (:tenant/mcp-allow-write? tenant))]
    [:div {:class "flex flex-col gap-4"}
     [:div {:class "flex items-center justify-between"}
      [:div {:class "font-semibold text-xl"} "MCP tool permissions"]
      [:div {:class "flex items-center gap-2"}
       (when saving?
         [:i.fal.fa-spinner.fa-spin.text-base-light-text-paler])
       [button/sm-secondary-hollow
        {:on-click (fn [_]
                     (rf/dispatch [::events/load-tool-permissions]))}
        [:span [:i.fal.fa-sync.mr-1] (loc/tr :refresh)]]]]
     [permissions-tabs active-tab]
     (when error
       [:div {:class "text-danger-regular text-sm"} error])
     (cond
       loading?
       [:div {:class "text-center py-6"}
        [:i.fal.fa-spinner.fa-pulse.text-base-light-text-paler]]

       (= active-tab :teams)
       [team-permissions-view
        data
        {:selected-team-id   selected-team-id
         :saving?            saving?
         :mcp-write-allowed? mcp-write-allowed?}]

       :else
       [user-permissions-view data selected-user-id])]))

(defn- connection-table
  [connections]
  (let [tenant        @(rf/subscribe [::session-sub/tenant])
        format-ts     #(ui-util/display-date-timestamp tenant %)
        revoking-id   @(rf/subscribe [::sub/revoking-id])
        revoking-all? @(rf/subscribe [::sub/revoking-all?])]
    [:div
     {:class "border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
     [:table
      {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
      [:thead
       [:tr
        {:class "*:px-3 *:py-3 *:text-left *:text-sm *:font-semibold *:text-base-light-text"}
        [:th (loc/tr :settings/mcp-admin-col-user)]
        [:th (loc/tr :settings/mcp-admin-col-token-name)]
        [:th (loc/tr :settings/mcp-admin-col-created)]
        [:th (loc/tr :settings/mcp-admin-col-last-used)]
        [:th ""]]]
      [:tbody
       {:class "divide-y divide-solid divide-base-light-border-paler"}
       (for [conn connections
             :let [{:keys [db/id token/plugin-connection-name
                           common/created-on token/last-used-at]} conn
                   acct-name (account-display-name conn)
                   busy?     (or revoking-all? (= revoking-id id))]]
         ^{:key (str id)}
         [:tr
          {:class "*:px-3 *:py-3 *:text-sm *:text-base-light-text *:align-middle"}
          [:td acct-name]
          [:td (or plugin-connection-name [:span {:class "text-base-light-text-paler"} "—"])]
          [:td (if created-on (format-ts created-on) "—")]
          [:td (if last-used-at (format-ts last-used-at) [:span {:class "text-base-light-text-paler"} (loc/tr :settings/mcp-admin-never)])]
          [:td
           {:class "text-right"}
           [button/sm-danger-solid
            {:on-click #(open-revoke-confirmation! id plugin-connection-name acct-name)
             :disabled? busy?}
            (if (= revoking-id id)
              [:i.fal.fa-spinner.fa-spin]
              (loc/tr :revoke))]]])]]]))

(defn- undo-op-row
  [{:keys [entity-id attribute value action reason prev-value]}]
  (let [will-undo? (#{"restore" "retract"} action)]
    [:tr
     {:class (str "*:px-3 *:py-2 *:text-xs *:align-top "
                  (if will-undo? "*:text-base-light-text" "*:text-base-light-text-paler"))}
     [:td {:class "font-mono"} (str entity-id)]
     [:td {:class "font-mono"} attribute]
     [:td {:class "font-mono max-w-xs truncate"} value]
     [:td
      (if will-undo?
        [:span {:class "text-success-regular font-medium"} (loc/tr :settings/mcp-undo-action-revert)]
        [:span {:class "text-base-light-text-paler"}
         (str (loc/tr :settings/mcp-undo-action-skip) (when reason (str " — " reason)))])]
     (when prev-value
       [:td {:class "font-mono max-w-xs truncate text-base-light-text-paler"} prev-value])]))

(defn- undo-preview-table
  [preview]
  [:div {:class "flex flex-col gap-3"}
   [:div {:class "flex gap-6 text-sm"}
    [:span [:b (:undo-count preview)] " " (loc/tr :settings/mcp-undo-will-revert)]
    [:span [:b (:skip-count preview)] " " (loc/tr :settings/mcp-undo-will-skip)]]
   [:div {:class "border border-solid border-base-light-border-paler rounded overflow-x-auto"}
    [:table {:class "min-w-full text-xs"}
     [:thead
      [:tr {:class "*:px-3 *:py-2 *:text-left *:font-semibold *:text-base-light-text *:bg-base-light-bg-paler"}
       [:th "Entity"]
       [:th "Attribute"]
       [:th "Value"]
       [:th "Action"]
       [:th "Prev value"]]]
     [:tbody {:class "divide-y divide-solid divide-base-light-border-paler"}
      (for [op (:ops preview)]
        ^{:key (str (:entity-id op) (:attribute op))}
        [undo-op-row op])]]]])

(defn- undo-section
  []
  (let [tx-id-input    @(rf/subscribe [::sub/undo-tx-id-input])
        preview        @(rf/subscribe [::sub/undo-preview])
        loading?       @(rf/subscribe [::sub/undo-loading?])
        undo-error     @(rf/subscribe [::sub/undo-error])
        undo-result    @(rf/subscribe [::sub/undo-result])
        parsed-tx-id   (when (re-matches #"\d+" (str tx-id-input))
                         (js/parseInt tx-id-input 10))]
    [:div {:class "flex flex-col gap-4 p-4 border border-solid border-warning-light rounded-lg"}
     [:div
      [:div {:class "font-semibold text-base"} (loc/tr :settings/mcp-undo-title)]
      [:p {:class "text-sm text-base-light-text-paler m-0 mt-1"} (loc/tr :settings/mcp-undo-description)]]

     [:div {:class "flex gap-2 items-end"}
      [:div {:class "flex flex-col gap-1 flex-1"}
       [:label {:class "text-xs font-medium"} (loc/tr :settings/mcp-undo-tx-id-label)]
       [:input {:class       "border border-solid border-base-light-border-pale rounded px-2 py-1.5 text-sm font-mono w-full"
                :type        "text"
                :placeholder "e.g. 13194139534312"
                :value       tx-id-input
                :on-change   (fn [event]
                               (rf/dispatch [::events/set-undo-tx-id-input
                                             (dg/event-target-value event)]))}]]
      [button/sm-secondary-hollow
       {:on-click #(when parsed-tx-id (rf/dispatch [::events/preview-undo parsed-tx-id]))
        :disabled? (or loading? (nil? parsed-tx-id))}
       (if loading?
         [:span [:i.fal.fa-spinner.fa-spin.mr-1] (loc/tr :settings/mcp-undo-previewing)]
         [:span [:i.fal.fa-search.mr-1] (loc/tr :settings/mcp-undo-preview)])]]

     (when undo-error
       [:div {:class "text-danger-regular text-sm"} undo-error])

     (when preview
       [:div {:class "flex flex-col gap-3"}
        [undo-preview-table preview]
        (when (pos? (:undo-count preview))
          [button/sm-danger-solid
           {:on-click #(open-undo-confirmation! parsed-tx-id (:undo-count preview))
            :disabled? loading?}
           [:span [:i.fal.fa-undo.mr-1] (loc/tr :settings/mcp-undo-confirm [(:undo-count preview)])]])])

     (when undo-result
       [:div {:class "flex flex-col gap-2"}
        [:div {:class "text-success-regular text-sm font-medium"}
         (loc/tr :settings/mcp-undo-success [(:undo-count undo-result) (:skip-count undo-result)])]
        [undo-preview-table undo-result]])]))

(defn view
  []
  (r/create-class
   {:component-did-mount    (fn [_] (rf/dispatch [::events/init]))
    :component-will-unmount (fn [_] (rf/dispatch [::events/cleanup]))
    :reagent-render
    (fn []
      (let [loading?             @(rf/subscribe [::sub/loading?])
            connections          @(rf/subscribe [::sub/connections])
            error                @(rf/subscribe [::sub/error])
            revoke-error         @(rf/subscribe [::sub/revoke-error])
            revoking-all?        @(rf/subscribe [::sub/revoking-all?])
            sessions-loading?    @(rf/subscribe [::sub/sessions-loading?])
            sessions             @(rf/subscribe [::sub/sessions])
            sessions-error       @(rf/subscribe [::sub/sessions-error])
            tenant               @(rf/subscribe [::session-sub/tenant])]
        [:div
         {:class "flex flex-col gap-6"}

         ;; Write-access toggle
         [write-access-toggle tenant]

         ;; Tool permissions section
         [tool-permissions-section]

         ;; Live sessions section
         [:div
          {:class "flex flex-col gap-4"}
          [:div
           {:class "flex items-center justify-between"}
           [:div {:class "font-semibold text-xl"} (loc/tr :settings/mcp-sessions-title)]
           [button/sm-secondary-hollow
            {:on-click #(rf/dispatch [::events/load-sessions])}
            [:span [:i.fal.fa-sync.mr-1] (loc/tr :refresh)]]]

          [:p {:class "text-sm text-base-light-text-paler m-0"}
           (loc/tr :settings/mcp-sessions-description)]

          (when sessions-error
            [:div {:class "text-danger-regular text-sm"} sessions-error])

          (cond
            sessions-loading?
            [:div {:class "text-center py-6"}
             [:i.fal.fa-spinner.fa-pulse.text-base-light-text-paler]]

            (empty? sessions)
            [:div
             {:class "p-4 border border-solid border-base-light-border-paler rounded text-center text-base-light-text-paler text-sm"}
             (loc/tr :settings/mcp-sessions-none)]

            :else
            [sessions-table sessions])]

         ;; Connections section
         [:div
          {:class "flex flex-col gap-4"}
          [:div
           {:class "flex items-center justify-between"}
           [:div
            {:class "font-semibold text-xl"}
            (loc/tr :settings/mcp-admin-title)]
           (when (seq connections)
             [button/sm-danger-solid
              {:on-click (fn [_]
                           (open-revoke-all-confirmation!))
               :disabled? revoking-all?}
              (if revoking-all?
                [:span [:i.fal.fa-spinner.fa-spin.mr-1] (loc/tr :settings/revoking)]
                [:span [:i.fal.fa-ban.mr-1] (loc/tr :settings/revoke-all)])])]

          [:p
           {:class "text-sm text-base-light-text-paler m-0"}
           (loc/tr :settings/mcp-admin-description)]

          (when error
            [:div {:class "text-danger-regular text-sm"} error])

          (when revoke-error
            [:div {:class "text-danger-regular text-sm"} revoke-error])

          (cond
            loading?
            [:div {:class "text-center py-6"}
             [:i.fal.fa-spinner.fa-pulse.text-base-light-text-paler]]

            (empty? connections)
            [:div
             {:class "p-6 border border-solid border-base-light-border-paler rounded text-center text-base-light-text-paler text-sm"}
             [:i.fal.fa-robot.mr-2]
             [:span (loc/tr :settings/mcp-admin-no-connections)]]

            :else
            [connection-table connections])]

         ;; Undo section
         [undo-section]]))}))
